import { FormInstance, message } from 'antd'
import { PropertyType, RentalInfoPeriod } from 'Modules'
import { useCallback, useEffect, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { useAccount } from 'wagmi'

import { AppAdmin } from 'api/app-admin'
import { PTOTypeEnum, TermsFlowEnum, TokenizeStatusEnum } from 'constants/pto'
import { SellingStatusEnum } from 'constants/pto-v2'
import { toRentalPeriodPayload } from 'extends/pto.extend'
import {
  cleanCoTrustedPartners,
  cleanGoldenTickets,
  cleanMetadata,
  INITIAL_CO_TRUSTED_PARTNER,
  INITIAL_GOLDEN_TICKET,
} from 'extends/pto-v2.extend'
import { customErrorContract } from 'hooks/contract/use-contract-viem'
import { useDeployCollection } from 'hooks/pto-v2/use-deploy-collection'
import { PAGES } from 'routes'
import { onCloseModalProcessing, onOpenModalProcessing } from 'stores/modal.store'
import { logError, logInfo } from 'utils/logger'

interface UseCreatePTOV2Props {
  form: FormInstance
}

/**
 * PTO v2 create flow: create PTO (Off Sale, no price/fee/timelines) then
 * deploy the collection contract via PTOFactoryUpgradeable.deployCollection.
 */
const useCreatePTOV2 = ({ form }: UseCreatePTOV2Props) => {
  const navigate = useNavigate()
  const [thumbnail, setThumbnail] = useState<string>('')
  const [thumbnails, setThumbnails] = useState<string[]>([])
  const [propertyTypes, setPropertyTypes] = useState<PropertyType[]>([])
  const [isSubmitting, setIsSubmitting] = useState(false)

  const { address } = useAccount()
  const { isContractManager, deployCollection } = useDeployCollection()

  const handleGetData = useCallback(async () => {
    try {
      const resListPropertyType = await AppAdmin.getListProperty()
      const types: PropertyType[] = []
      if (resListPropertyType?.data.success) {
        resListPropertyType?.data.data.map((item: PropertyType) => {
          types.push({
            ...item,
            id: Number(item.id),
          })
        })
        setPropertyTypes(types)
      }
      const formValues = {
        metadata: {},
        goldenTickets: [INITIAL_GOLDEN_TICKET],
        coTrustedPartners: [INITIAL_CO_TRUSTED_PARTNER],
        status: true,
        isShowDetail: true,
        isShowList: true,
      }
      form.setFieldsValue(formValues)
    } catch (error: any) {
      logError('Upload error:', error)
    }
  }, [form])

  const handleSubmit = async () => {
    try {
      setIsSubmitting(true)
      const values = await form.validateFields()
      const {
        type,
        ptoType,
        name,
        contractName,
        country,
        description = '',
        status,
        isFeatured,
        isShowDetail,
        isShowList,
        latitude,
        longitude,
        propertyItems,
        metadata: formMetadata,
        maxNFTSupply,
        royalty,
        tradeUnlockTime,
        ownerAddress,
        goldenTickets: formGoldenTickets,
        typeSocial,
        rentalPeriods,
        isLeasingInProgress,
        terms,
        coTrustedPartners: formCoTrustedPartners,
      } = values

      const metadata = cleanMetadata(formMetadata)
      const goldenTickets = cleanGoldenTickets(formGoldenTickets)
      const coTrustedPartners = cleanCoTrustedPartners(formCoTrustedPartners)

      // Payload follows POST /admin/pto v2 body spec - display (status) is set
      // via PATCH /admin/pto/:id/contract right after deploying the collection
      const params = {
        fullName: name,
        contractName: (contractName || '').trim(),
        sellingStatus: SellingStatusEnum.OFF_SALE,
        country: country,
        description: description,
        isFeatured: !!isFeatured,
        isShowDetail: !!isShowDetail,
        isShowList: !!isShowList,
        type: type,
        ptoType: ptoType,
        latitude: latitude,
        longitude: longitude,
        propertyItems: propertyItems,
        metadata: metadata,
        thumbnail: thumbnail || '',
        thumbnails: thumbnails || [],
        ownerAddress: ownerAddress,
        tradeUnlockTime: tradeUnlockTime || 0,
        maxNFTSupply,
        royaltyFee: royalty,
        goldenTickets,
        typeSocial: typeSocial || {},
        isLeasingInProgress: !!isLeasingInProgress,
        terms: {
          content: terms || '',
          flowType: TermsFlowEnum.PURCHASE,
        },
        coTrustedPartners,
      }
      onOpenModalProcessing()
      logInfo('Submitting PTO v2 with params:', params)

      const results = await AppAdmin.ptoV2Create(params)
      if (results.status === 200) {
        const createdPto = results.data.data

        if (ptoType === PTOTypeEnum.RENTAL_PTO && rentalPeriods?.length) {
          for (const period of rentalPeriods as RentalInfoPeriod[]) {
            await AppAdmin.createRentalInfo(createdPto.externalId, toRentalPeriodPayload(period))
          }
        }

        if (!address) {
          message.error('Please connect wallet!')
        } else if (!isContractManager) {
          message.error('Your wallet address does not have permission to create PTO!')
        } else {
          try {
            const contractAddress = await deployCollection(address, createdPto)
            if (contractAddress) {
              await AppAdmin.ptoUpdateContract(createdPto.externalId, {
                contractAddress,
                status: status ? TokenizeStatusEnum.ACTIVE : TokenizeStatusEnum.INACTIVE,
              })
              message.success('PTO created successfully!')
            }
          } catch (error) {
            const errorMessage = customErrorContract(error)
            message.error(errorMessage || 'Deploy collection failed, you can deploy it later!')
            logError('Error while deploying collection:', error)
          }
        }
        navigate(PAGES.PTO_V2)
      }
    } catch (error: any) {
      const errorMessage = customErrorContract(error)
      message.error(errorMessage || 'Error while submitting form')
      logError('Error while submitting form:', errorMessage)
    } finally {
      onCloseModalProcessing()
      setIsSubmitting(false)
    }
  }

  const handleCancel = () => {
    navigate(PAGES.PTO_V2)
  }

  useEffect(() => {
    handleGetData()
  }, [handleGetData])

  return {
    thumbnail,
    setThumbnail,
    thumbnails,
    setThumbnails,
    propertyTypes,
    isSubmitting,
    handleSubmit,
    handleCancel,
  }
}

export default useCreatePTOV2
